import React, { useEffect, useState } from 'react';
import { useTutorialSafe, tutorialEventListeners, type TutorialEvent } from '@/app/contexts/TutorialContext';
import { TutorialSpotlight } from './TutorialSpotlight';
import { TutorialTooltipDraggable } from './TutorialTooltipDraggable';
import { TutorialProgress } from './TutorialProgress';
import { TutorialHandCursor } from './TutorialHandCursor';
import { SkipForward, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { tutorialSound } from '@/app/utils/tutorialSounds';

export function TutorialOverlay() {
  const tutorial = useTutorialSafe();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [elementNotFound, setElementNotFound] = useState(false);
  const [conditionMet, setConditionMet] = useState(false);

  // Get tutorial state and methods (but handle null case)
  const state = tutorial?.state;
  const currentStep = tutorial?.currentStep;
  const nextStep = tutorial?.nextStep;
  const previousStep = tutorial?.previousStep;
  const skipTutorial = tutorial?.skipTutorial;
  const stopTutorial = tutorial?.stopTutorial;
  const resumeTutorial = tutorial?.resumeTutorial;

  // Find target element
  useEffect(() => {
    if (!currentStep?.target?.selector) {
      setTargetElement(null);
      setTargetRect(null);
      setElementNotFound(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = 20; // Try for 2 seconds

    const findElement = () => {
      const element = document.querySelector(currentStep.target!.selector!) as HTMLElement;
      if (element) {
        setTargetElement(element);
        setTargetRect(element.getBoundingClientRect());
        setElementNotFound(false);
        console.log('[Tutorial] Found element:', currentStep.target!.selector);
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(findElement, 100);
        } else {
          // Element not found after all attempts
          console.warn('[Tutorial] Element not found:', currentStep.target!.selector);
          setElementNotFound(true);
          setTargetElement(null);
          setTargetRect(null);
        }
      }
    };

    findElement();
  }, [currentStep]);

  // Update rect on scroll/resize
  useEffect(() => {
    if (!targetElement) return;

    const updateRect = () => {
      setTargetRect(targetElement.getBoundingClientRect());
    };

    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [targetElement]);

  // Event-Listener für conditionMet - wenn User die erwartete Aktion ausführt
  useEffect(() => {
    // Reset bei Schritt-Wechsel
    setConditionMet(false);

    if (!currentStep?.waitForEvent || !state?.isActive) {
      return;
    }

    const handleEvent = (event: TutorialEvent) => {
      // Prüfe ob Event-Type übereinstimmt
      if (currentStep.waitForEvent!.type !== event.type) {
        return;
      }

      // Wenn payload vorhanden ist, prüfen ob es matched
      if (currentStep.waitForEvent!.payload) {
        const expectedPayload = currentStep.waitForEvent!.payload as Record<string, any>;
        const actualPayload = event as Record<string, any>;

        // Check if all expected payload keys match
        const matches = Object.keys(expectedPayload).every(key => {
          return actualPayload[key] === expectedPayload[key];
        });

        if (!matches) {
          return;
        }
      }

      // Event matched! Zeige "Perfekt!" Feedback
      console.log('[TutorialOverlay] Event matched, showing success feedback', event);
      setConditionMet(true);

      // Sound abspielen
      if (state.soundEnabled) {
        tutorialSound.playSuccess();
      }

      // KEIN Auto-Advance mehr! User muss manuell "Weiter" klicken
      // Der "Weiter"-Button wird durch conditionMet enabled
    };

    tutorialEventListeners.add(handleEvent);
    return () => {
      tutorialEventListeners.delete(handleEvent);
    };
  }, [currentStep, state?.isActive, state?.soundEnabled]);

  // ESC-Hotkey zum Beenden (doppelt drücken)
  useEffect(() => {
    if (!state?.isActive) return;

    let escPressCount = 0;
    let escTimeout: NodeJS.Timeout;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        escPressCount++;
        if (escPressCount === 1) {
          // Erste ESC: Zeige Hinweis in Console (später mit Toast)
          console.log('[Tutorial] ESC gedrückt - nochmal drücken zum Beenden');
          escTimeout = setTimeout(() => {
            escPressCount = 0;
          }, 2000);
        } else if (escPressCount === 2) {
          // Zweite ESC: Beenden
          clearTimeout(escTimeout);
          handleSkipTutorial();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (escTimeout) clearTimeout(escTimeout);
    };
  }, [state?.isActive]);
  
  // Zeige Resume-Button wenn Tutorial pausiert ist
  if (state?.progress.canResume && resumeTutorial) {
    return (
      <div className="fixed inset-0 z-[10000] pointer-events-none flex items-center justify-center">
        <div className="pointer-events-auto">
          <Button
            onClick={resumeTutorial}
            size="lg"
            className="gap-2 shadow-2xl"
            style={{
              backgroundColor: 'var(--md-primary)',
              color: 'var(--md-on-primary)',
            }}
          >
            <Play className="w-5 h-5" />
            Tutorial fortsetzen
          </Button>
        </div>
      </div>
    );
  }

  if (!state?.isActive || !currentStep) {
    return null;
  }

  const handleSkipTutorial = () => {
    if (skipTutorial && confirm('Möchten Sie das Tutorial wirklich beenden?')) {
      skipTutorial();
    }
  };

  const handleNext = () => {
    if (nextStep) {
      if (state.soundEnabled) {
        tutorialSound.playSuccess();
      }
      nextStep();
    }
  };

  // Bestimme Hand Cursor Action
  const handCursorAction = currentStep.action === 'click' ? 'click' : 
                          currentStep.action === 'drag-drop' ? 'drag' : 'none';

  return (
    <div className="fixed inset-0 z-[10000] pointer-events-none">
      {/* Dark Overlay */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity duration-300 pointer-events-none"
      />

      {/* Spotlight on target element */}
      {targetRect && (
        <TutorialSpotlight
          rect={targetRect}
          offset={currentStep.target?.offset}
        />
      )}

      {/* Animated Hand Cursor */}
      <TutorialHandCursor
        targetRect={targetRect}
        action={handCursorAction}
      />

      {/* Tooltip - NEU: Draggable */}
      {nextStep && previousStep && stopTutorial && (
        <TutorialTooltipDraggable
          step={currentStep}
          targetRect={targetRect}
          onNext={handleNext}
          onPrevious={previousStep}
          onSkip={handleSkipTutorial}
          onClose={stopTutorial}
          conditionMet={conditionMet}
        />
      )}

      {/* Progress Bar */}
      <TutorialProgress />

      {/* Skip Button (top right) */}
      <div className="absolute top-4 right-4 pointer-events-auto">
        <Button
          onClick={handleSkipTutorial}
          variant="outline"
          size="sm"
          className="gap-2 shadow-lg"
          style={{
            backgroundColor: 'var(--md-surface)',
            borderColor: 'var(--md-outline)',
            color: 'var(--md-on-surface)',
          }}
        >
          <SkipForward className="w-4 h-4" />
          Tutorial beenden
        </Button>
      </div>
    </div>
  );
}